/**
 * Cross-boundary contracts shared between the gen-2-chat host and the
 * `@wippy-fe/*` consumer packages.
 *
 * **Pure types and string-name constants.** No runtime code, no
 * `@wippy-fe/proxy` dependency. Importing from this module never loads
 * the proxy globals checker — it is safe to import from anywhere in the
 * host source tree without tripping the `host-import-guard` Vite plugin
 * (this package is on the allowlist).
 *
 * **Status: Draft 1 (preview).** Field names and shapes may change
 * between minor releases until a production consumer drives the corner
 * cases out.
 */

// ─── Layout bus envelopes ────────────────────────────────────────────

/**
 * In-tab broadcast envelope. Same shape on both sides of the bus —
 * iframe panels, host-mounted web components, and headless services
 * all see this object.
 */
export interface BroadcastEnvelope {
  channel: string
  payload: unknown
  sourcePanelId: string | null
  targetPanelId: string | null
}

/**
 * Panel-bound layout bus exposed on `el.__wippyHostBus` for web
 * components mounted as `kind: 'component'` panels. Source / target
 * attribution is set by the host — publishers cannot spoof it.
 */
export interface LayoutBusBound {
  /** 1:N broadcast — every other panel receives, sender excluded. */
  broadcast: (channel: string, payload: unknown) => void
  /** 1:1 send — only `target` receives. Unknown targets are no-ops. */
  send: (target: string, channel: string, payload: unknown) => void
  /** Subscribe; returns an `off()` for cleanup. */
  subscribe: (handler: (env: BroadcastEnvelope) => void) => () => void
}

// ─── Layout mutation targets ─────────────────────────────────────────

/**
 * Drop target positions (relative to a slot rect). Reserved for the
 * future panelDrag capability; declared in v1 for forward compat.
 */
export type DropPosition = 'left' | 'right' | 'top' | 'bottom' | 'center' | 'none'

/**
 * Slot size grammar (mirrors `@wippy-fe/layout` `SizeValue`). Tight
 * whitelist — `clamp()`, `minmax()`, `min-content`, `calc()`, em/rem/
 * vh/vw are rejected at validateLayout time. See `@wippy-fe/layout`
 * `helpers.ts` "Size grammar" for rationale.
 */
export type SizeValue
  = | 'auto'
    | `${number}fr`
    | `${number}px`
    | `${number}%`

/** Pixel-only size, used for min/max and drawer dimensions. */
export type PixelSize = `${number}px`

/**
 * Target for addPanel / movePanel. `relativeTo` names a panel already in
 * the tree; `position` picks the drop relative to that panel's rect.
 * Omit `relativeTo` to attach to the root tree (position becomes
 * direction-aware).
 */
export interface PanelTarget {
  relativeTo?: string
  position: DropPosition
  size?: SizeValue
}

// ─── Global-name constants ───────────────────────────────────────────
//
// These literal strings name the four `window.*` globals that the host
// writes and consumer code reads. Both sides MUST agree on the spelling
// — moving a typo here would silently break communication across the
// host/proxy boundary. No `Window` augmentation is declared in this
// package because the augmentation references host-side types
// (`AppConfig`, `ProxyApiInstance`); host-side `src/shared/constants/global.ts`
// keeps the `declare global` block.

export const GLOBAL_CONFIG_VAR = '__WIPPY_APP_CONFIG__' as const
export const GLOBAL_PROXY_CONFIG_VAR = '__WIPPY_PROXY_CONFIG__' as const
export const GLOBAL_API_PROVIDER = '__WIPPY_APP_API__' as const
export const GLOBAL_WEB_COMPONENT_CACHE = '__WIPPY_WEB_COMPONENT_CACHE__' as const

// ─── HTML data-role markers ─────────────────────────────────────────
//
// `data-role` attributes that the host and dev-proxy use to find anchor
// elements in the consuming app's `app.html`. The host removes / inserts
// scripts at the `@wippy/scripts` element; dev-proxy reads the consuming
// app's package.json from the `@wippy/package` JSON script (injected at
// build time by `@wippy-fe/vite-plugin`'s `wippyPackagePlugin()`).

export const WIPPY_SCRIPTS_DATA_ROLE = '@wippy/scripts' as const
export const WIPPY_PACKAGE_DATA_ROLE = '@wippy/package' as const
